const SESSION_ID_KEY = 'data360.auditSessionId';
const SESSION_STARTED_KEY = 'data360.auditSessionStartedAt';
const SESSION_LAST_ACTIVITY_KEY = 'data360.auditSessionLastActivityAt';

/** Reuse the same session while the user is active within this window (1 hour). */
export const AUDIT_SESSION_TTL_MS = 60 * 60 * 1000;

function generateSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `S-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
    sessionStorage.setItem(key, value);
  } catch {
    /* ignore quota / private mode */
  }
}

function removeStorage(key: string): void {
  try {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function parseTime(iso: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

function isSessionExpired(): boolean {
  const last = parseTime(readStorage(SESSION_LAST_ACTIVITY_KEY));
  if (last == null) return true;
  return Date.now() - last > AUDIT_SESSION_TTL_MS;
}

function touchSessionActivity(): void {
  writeStorage(SESSION_LAST_ACTIVITY_KEY, new Date().toISOString());
}

/** Read current audit session id (may be expired — prefer ensureAuditSession). */
export function getAuditSessionId(): string | null {
  return readStorage(SESSION_ID_KEY);
}

export function getAuditSessionStartedAt(): string | null {
  return readStorage(SESSION_STARTED_KEY);
}

export function getAuditSessionLastActivityAt(): string | null {
  return readStorage(SESSION_LAST_ACTIVITY_KEY);
}

/**
 * Start a brand-new session — call on successful login.
 * Also used when the previous session is older than 1 hour.
 */
export function startNewAuditSession(): string {
  const sessionId = generateSessionId();
  const now = new Date().toISOString();
  writeStorage(SESSION_ID_KEY, sessionId);
  writeStorage(SESSION_STARTED_KEY, now);
  writeStorage(SESSION_LAST_ACTIVITY_KEY, now);
  return sessionId;
}

/**
 * Reuse session if last activity was within 1 hour; otherwise start a new session.
 * Updates last-activity timestamp on every call.
 */
export function ensureAuditSession(): string {
  const existing = getAuditSessionId();
  if (existing && !isSessionExpired()) {
    touchSessionActivity();
    return existing;
  }
  return startNewAuditSession();
}

/** @deprecated Use startNewAuditSession on login or ensureAuditSession elsewhere. */
export function initAuditSession(): string {
  return startNewAuditSession();
}

/** End audit session on logout. */
export function clearAuditSession(): void {
  removeStorage(SESSION_ID_KEY);
  removeStorage(SESSION_STARTED_KEY);
  removeStorage(SESSION_LAST_ACTIVITY_KEY);
}
